import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Modal, Typography, generateUtilityClasses } from '@mui/material'
import { styled } from '@mui/system'
import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import DropdownFieldSearch from '../../../../ui/DropdownFieldSearch'
import { CustomCircular, TextField } from '../../../../ui'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import { useAddCarPurchaseTypeMutation, useEditCarPurchaseTypeMutation, useLazyGetCarPurchaseTypeDataQuery } from '../../../../services'
import { UspConfigurationCarPurchaseTypeView } from '../../../../models/apiTypes'

const classes = generateUtilityClasses('Box', ['root', 'row', 'buttonWrap', 'saveBtn'])

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '390px',
    backgroundColor: theme.palette.background.main,
    boxShadow: 24,
    padding: theme.spacing(3),
  },
  [`& .${classes.row}`]: {
    margin: theme.spacing(1, 0, 2, 0),
  },
  [`& .${classes.buttonWrap}`]: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
    padding: theme.spacing(3, 0, 2, 0),
    marginTop: 'auto',
  },
  [`& .${classes.saveBtn}`]: {
    marginLeft: theme.spacing(2),
  },
}))

interface Props {
  rowData: UspConfigurationCarPurchaseTypeView
  open: boolean
  onClose: () => void
  type: string
  submit: (...args: any) => any
}

const AddEditPurchaseTypeModal = ({ open, onClose, rowData, type, submit }: Props) => {
  const [initialValues, setInitialValues] = useState({})
  const [addCarPurchaseType, { isLoading: isLoadingCarPurchaseType, isSuccess: isSuccessCarPurchaseType }] = useAddCarPurchaseTypeMutation()
  const [editCarPurchaseType, { isLoading: isLoadingEditCarPurchaseType, isSuccess: isSuccessEditCarPurchaseType }] = useEditCarPurchaseTypeMutation()
  const [getCarPurchaseTypeData, { isLoading: isLoadingGetCarPurchaseType, isSuccess: isSuccessGetCarPurchaseType, data }] =
    useLazyGetCarPurchaseTypeDataQuery()

  const handleSubmit = (values: any) => {
    if (type === 'edit') {
      editCarPurchaseType({
        carPurchaseTypeId: rowData?.ID?.toString(),
        uspConfigurationCarPurchaseTypeAddEdit: {
          purchaseType: values.Description,
          isActive: values.IsActive,
        },
      })
    } else {
      addCarPurchaseType({
        uspConfigurationCarPurchaseTypeAddEdit: {
          purchaseType: values.Description,
          isActive: values.IsActive,
        },
      })
    }
  }

  useEffect(() => {
    if (open && rowData && type === 'edit')
      getCarPurchaseTypeData({
        carPurchaseTypeId: rowData?.ID?.toString() as string,
      })
  }, [open, type, rowData])

  useEffect(() => {
    if (type === 'add') {
      setInitialValues({
        Description: '',
        IsActive: true,
      })
    }
    if (isSuccessGetCarPurchaseType && type === 'edit') {
      setInitialValues({
        Description: data?.PurchaseType,
        IsActive: data?.IsActive,
      })
    }
  }, [isSuccessGetCarPurchaseType, data, type])

  useEffect(() => {
    if (isSuccessCarPurchaseType || isSuccessEditCarPurchaseType) {
      onClose()
      submit()
    }
  }, [isSuccessCarPurchaseType, isSuccessEditCarPurchaseType])

  const isLoading = useMemo(
    () => isLoadingGetCarPurchaseType || isLoadingCarPurchaseType || isLoadingEditCarPurchaseType,
    [isLoadingGetCarPurchaseType, isLoadingCarPurchaseType, isLoadingEditCarPurchaseType],
  )

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Car Purchase Type</DialogTitle>
      <Form onSubmit={handleSubmit} initialValues={initialValues}>
        {({ handleSubmit }: any) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} py={1}>
                <Grid item xs={12}>
                  <TextField required name='Description' label='Description' fullWidth />
                </Grid>

                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='IsActive'
                    label='Is Active?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <LoadingButton type='submit' className={classes.saveBtn} variant='contained'>
                Save
              </LoadingButton>
              <Button variant='contained' color='error' onClick={onClose}>
                Cancel
              </Button>
            </DialogActions>
          </form>
        )}
      </Form>
      <CustomCircular open={isLoading} />
    </Dialog>
  )
}

export default AddEditPurchaseTypeModal
